import React from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import TuneIcon from '@mui/icons-material/Tune';
import StatusFilterChips from 'components/StatusFilterChips';
import { pickStatusColor, WordStatus } from './statusMeta';
import { StatusFilter, UseWordsViewState, ViewMode } from './useWordsView';

interface DictionaryFilterBarProps {
  view: UseWordsViewState;
  /**
   * Receives the gear-button element so the consumer can anchor a popover
   * to it (the design calls for the settings popup to drop down right below
   * the gear, not float in the screen centre).
   */
  onOpenSettings?: (anchor: HTMLElement) => void;
  /**
   * When true, the GearBtn renders in pressed state (border + bg + color all
   * shift to primary tint). Sync from the parent that owns the popover/modal.
   */
  settingsOpen?: boolean;
}

const ViewToggleButton: React.FC<{
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}> = ({ active, icon, label, onClick }) => (
  <Box
    component={'button'}
    onClick={onClick}
    title={label}
    sx={{
      alignItems: 'center',
      bgcolor: active ? 'primary.main' : 'transparent',
      border: 'none',
      borderRadius: 999,
      color: active ? 'common.white' : 'text.secondary',
      cursor: 'pointer',
      display: 'inline-flex',
      fontSize: 13,
      fontWeight: 700,
      gap: 0.75,
      height: 32,
      px: 1.625,
      transition: 'all .14s ease',
    }}
  >
    {icon}
    {label}
  </Box>
);

const DictionaryFilterBar: React.FC<DictionaryFilterBarProps> = ({
  view,
  onOpenSettings,
  settingsOpen = false,
}) => {
  const theme = useTheme();
  const statusOptions = view.statusFilterOptions.map((opt) => ({
    value: opt.value,
    label: opt.label,
    color:
      opt.value !== 'all'
        ? pickStatusColor(opt.value as WordStatus, theme.palette.mode)
        : undefined,
  }));
  return (
    <Stack
      direction={'row'}
      alignItems={'center'}
      spacing={1.5}
      sx={{ flexWrap: 'wrap', rowGap: 1.5, mb: 2 }}
    >
      <TextField
        size={'small'}
        value={view.search}
        onChange={(e) => view.setSearch(e.target.value)}
        placeholder={'Пошук слова або перекладу…'}
        InputProps={{
          startAdornment: (
            <InputAdornment position={'start'}>
              <SearchIcon sx={{ color: 'text.secondary', fontSize: 19 }} />
            </InputAdornment>
          ),
          sx: {
            bgcolor: 'background.paper',
            borderRadius: 999,
            height: 40,
            fontSize: 14,
            '& fieldset': { borderRadius: 999 },
          },
        }}
        sx={{
          flex: '1 1 220px',
          maxWidth: 320,
        }}
      />

      <StatusFilterChips
        value={view.statusFilter}
        options={statusOptions}
        onChange={(next) => view.setStatusFilter(next as StatusFilter)}
      />


      <Box sx={{ flex: 1 }} />

      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'primary.tableBorder',
          borderRadius: 999,
          display: 'inline-flex',
          gap: '2px',
          p: '3px',
        }}
      >
        <ViewToggleButton
          active={view.viewMode === 'table'}
          icon={<ViewListIcon sx={{ fontSize: 18 }} />}
          label={'Список'}
          onClick={() => view.setViewMode('table' as ViewMode)}
        />
        <ViewToggleButton
          active={view.viewMode === 'cards'}
          icon={<GridViewIcon sx={{ fontSize: 18 }} />}
          label={'Плитка'}
          onClick={() => view.setViewMode('cards' as ViewMode)}
        />
      </Box>

      <Box
        component={'button'}
        onClick={() => view.setHideKnown(!view.hideKnown)}
        sx={{
          alignItems: 'center',
          bgcolor: view.hideKnown
            ? (t) =>
                t.palette.mode === 'light'
                  ? 'rgba(59,44,147,0.10)'
                  : 'rgba(155,124,255,0.18)'
            : 'background.paper',
          border: '1px solid',
          borderColor: view.hideKnown ? 'primary.main' : 'primary.tableBorder',
          borderRadius: 999,
          color: view.hideKnown ? 'primary.main' : 'text.secondary',
          cursor: 'pointer',
          display: 'inline-flex',
          fontSize: 13,
          fontWeight: 700,
          gap: 0.875,
          height: 34,
          px: 1.5,
          transition: 'all .14s ease',
        }}
      >
        {view.hideKnown ? (
          <VisibilityOffIcon sx={{ fontSize: 18 }} />
        ) : (
          <VisibilityIcon sx={{ fontSize: 18 }} />
        )}
        {'Сховати відомі'}
      </Box>

      {onOpenSettings && (
        <Tooltip
          title={
            view.viewMode === 'table'
              ? 'Налаштування таблиці'
              : 'Налаштування карток'
          }
          placement={'top'}
        >
          <IconButton
            size={'small'}
            onClick={(e) => onOpenSettings(e.currentTarget)}
            sx={(t) => ({
              bgcolor: settingsOpen
                ? t.palette.mode === 'light'
                  ? 'rgba(59,44,147,0.10)'
                  : 'rgba(155,124,255,0.18)'
                : 'background.paper',
              border: '1px solid',
              borderColor: settingsOpen
                ? 'primary.main'
                : 'primary.tableBorder',
              borderRadius: 1.25,
              color: settingsOpen ? 'primary.main' : 'text.secondary',
              height: 38,
              transition:
                'background-color .14s ease, border-color .14s ease, color .14s ease',
              width: 38,
            })}
          >
            <TuneIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
};

export default DictionaryFilterBar;
